import { Controller } from "@hotwired/stimulus"

// Handles allowance input interactions, including "Ganzer Tag"
export default class extends Controller {
  static targets = ["hoursInput", "wholeDayCheckbox"]
  static values = {
    wholeDayPlaceholder: String,
    // "Eingabeschritt" in minutes; 0 = free input.
    stepMinutes: { type: Number, default: 0 },
  }

  // Rounds a typed value to the nearest step when the field is left, the same way the
  // server does on save (Timesheet::AllowanceInputParser.snap).
  snapToStep() {
    if (!this.hasHoursInputTarget || this.hoursInputTarget.readOnly || this.stepMinutesValue <= 0)
      return

    const hours = parseFloat(this.hoursInputTarget.value)
    if (Number.isNaN(hours)) return

    const steps = Math.round((hours * 60) / this.stepMinutesValue)
    this.setHours(steps)
  }

  // Arrow keys move by the step. The browser does that on its own only when the field
  // has a decimal `step`; a 5-minute step and an old off-step value render "any", where
  // its arrows would move by a whole hour.
  stepByArrow(event) {
    if (this.stepMinutesValue <= 0 || this.hoursInputTarget.readOnly) return
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return

    event.preventDefault()
    const hours = parseFloat(this.hoursInputTarget.value) || 0
    const current = Math.round((hours * 60) / this.stepMinutesValue)
    this.setHours(Math.max(0, current + (event.key === "ArrowUp" ? 1 : -1)))
  }

  setHours(steps) {
    const value = String(Number(((steps * this.stepMinutesValue) / 60).toFixed(4)))
    if (value === this.hoursInputTarget.value) return

    this.hoursInputTarget.value = value
    // Keeps the Zulagen header count in step (0.1 h at 15 min snaps to 0).
    this.hoursInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // "Ganzer Tag" books the entry's net work time, which the server works out on save
  // (ConfigurableTimeSheet#allowance_effective_input). The hours field has nothing to
  // say then, so it is emptied and locked rather than filled with a number that could
  // be stale the moment the times change.
  connect() {
    if (this.hasHoursInputTarget && this.wholeDayChecked) this.lockForWholeDay()
  }

  toggleWholeDay() {
    if (!this.hasHoursInputTarget) return

    if (this.wholeDayChecked) {
      this.lockForWholeDay()
    } else {
      const input = this.hoursInputTarget
      input.readOnly = false
      input.placeholder = this.originalPlaceholder ?? ""
      // Voice entry may have filled the field in the meantime; that value wins.
      if (input.value === "") input.value = this.originalHoursValue ?? ""
    }

    // Keeps the Zulagen header count in step.
    this.hoursInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Voice entry and copy-previous-day write into the field without touching the box. Hours
  // they bring win over "Ganzer Tag", or the locked field would show a number the save
  // ignores; a whole_day they bring as well is applied right after and locks it again.
  releaseWholeDay() {
    const input = this.hoursInputTarget
    if (!input.readOnly || input.value === "" || !this.wholeDayChecked) return

    this.wholeDayCheckboxTarget.checked = false
    this.toggleWholeDay()
  }

  lockForWholeDay() {
    const input = this.hoursInputTarget
    if (!input.readOnly) {
      this.originalHoursValue = input.value
      this.originalPlaceholder = input.placeholder
    }
    input.value = ""
    input.placeholder = this.wholeDayPlaceholderValue
    input.readOnly = true
  }

  get wholeDayChecked() {
    return this.hasWholeDayCheckboxTarget && this.wholeDayCheckboxTarget.checked
  }
}
